import React, {useMemo, useState} from 'react';
import DataGrid from "./DataGrid";
import AdminSectionViewMode from "./AdminSectionViewMode";
import LoaderContainer from "./LoaderContainer";
import {getRowStatusClass, getTileStatusClass} from "../utils/adminStatus";

const AdminDataView = ({
    section,
    rows = [],
    columns = [],
    loading = false,
    dataGridProps = {},
    tileClassName = 'grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4 gap-4',
    renderTile = null,
    resolvePhotoUrl = null,
    resolveRowStatus = (row) => row?.status
}) => {
    const [isFieldsOpen, setIsFieldsOpen] = useState(false);
    const fields = useMemo(() => columns.filter((c) => c.key !== 'actions'), [columns]);
    const actionColumn = useMemo(() => columns.find((c) => c.key === 'actions'), [columns]);
    const fieldKeys = useMemo(() => fields.map((f) => f.key), [fields]);

    return (
        <AdminSectionViewMode section={section} availableFieldKeys={fieldKeys}>
            {({viewMode, setViewMode, sectionFields, setSectionFields, isReady, error}) => {
                const visibleKeys = sectionFields?.[viewMode]?.length ? sectionFields[viewMode] : fieldKeys;
                const visibleFields = fields.filter((field) => visibleKeys.includes(field.key));
                const visibleColumns = columns.filter((column) => {
                    if (column.key === 'actions') return true;
                    return visibleKeys.includes(column.key);
                });
                const showPhoto = visibleFields.some((field) => field.key === 'photo');
                const baseRowClassRenderer = dataGridProps?.rowClassRenderer;
                const mergedDataGridProps = {
                    ...dataGridProps,
                    rowClassRenderer: (row) => {
                        const extraClass = baseRowClassRenderer ? baseRowClassRenderer(row) : null;
                        const statusClass = getRowStatusClass(resolveRowStatus(row));
                        return [extraClass, statusClass].filter(Boolean).join(' ') || null;
                    }
                };

                return (
                    <div>
                        <div className="flex items-center justify-end mb-4 gap-3">
                            <button
                                type="button"
                                className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700"
                                onClick={() => setIsFieldsOpen((prev) => !prev)}
                            >
                                Pole
                            </button>
                            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                                <button
                                    type="button"
                                    className={`px-3 py-2 text-sm ${viewMode === 'table' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}
                                    onClick={() => setViewMode('table')}
                                >
                                    Tabulka
                                </button>
                                <button
                                    type="button"
                                    className={`px-3 py-2 text-sm ${viewMode === 'tiles' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}
                                    onClick={() => setViewMode('tiles')}
                                >
                                    Dlaždice
                                </button>
                            </div>
                        </div>

                        {isFieldsOpen ? (
                            <div className="mb-4 p-3 border border-gray-200 rounded-md bg-gray-50">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {fields.map((field) => {
                                        const checked = visibleKeys.includes(field.key);
                                        return (
                                            <label key={field.key} className="inline-flex items-center gap-2 text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={(e) => {
                                                        const next = e.target.checked
                                                            ? [...visibleKeys, field.key]
                                                            : visibleKeys.filter((key) => key !== field.key);
                                                        setSectionFields(viewMode, next.length ? next : [field.key]);
                                                    }}
                                                />
                                                <span>{String(field.name || field.key)}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}

                        {error ? <div className="text-sm text-red-600 mb-3">{error?.message || 'Nastala chyba.'}</div> : null}

                        <LoaderContainer loading={!isReady || loading}>
                            {viewMode === 'table' ? (
                                <DataGrid rows={rows} columns={visibleColumns} {...mergedDataGridProps}/>
                            ) : (
                                <div className={tileClassName}>
                                    {rows.map((row) => (
                                        <React.Fragment key={row._id || row.id || row.key}>
                                            {renderTile ? renderTile({
                                                row,
                                                visibleFields,
                                                actionColumn
                                            }) : (
                                                <div className={`border rounded-lg bg-white shadow-sm overflow-hidden ${getTileStatusClass(resolveRowStatus(row)) || 'border-gray-200'}`}>
                                                    {showPhoto ? (
                                                        <div className="aspect-[16/9] bg-gray-100">
                                                            {resolvePhotoUrl?.(row) ? (
                                                                <img
                                                                    src={resolvePhotoUrl(row)}
                                                                    alt={String(row?.buybackNumber || row?.saleNumber || row?.name || 'Fotografie')}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">
                                                                    Bez fotografie
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : null}
                                                    <div className="p-4">
                                                    <div className="space-y-1 text-sm">
                                                        {visibleFields.map((field) => (
                                                            <div key={field.key}>
                                                                <span className="text-gray-500">{String(field.name || field.key)}:</span>{' '}
                                                                <span>{field.renderCell ? field.renderCell({row}) : String(row?.[field.key] ?? '-')}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {actionColumn?.renderCell ? (
                                                        <div className="mt-3">
                                                            {actionColumn.renderCell({row})}
                                                        </div>
                                                    ) : null}
                                                    </div>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </LoaderContainer>
                    </div>
                );
            }}
        </AdminSectionViewMode>
    );
};

export default AdminDataView;
